<template>
  <form @submit.prevent="submit">
    <div class="flex grid-cols-3 flex-col gap-2 md:grid md:gap-4">
      <InputField :label="t('comment')" name="comment" :disabled="readonly" />
      <BookingRangePicker
        :start-date="formValues.startDate"
        :end-date="formValues.endDate"
        class="col-span-2"
        :car-id="props.car.id"
        :other-price-changing-module-is-active="false"
        :start-date-title="t('maintenanceStart')"
        :end-date-title="t('maintenanceEnd')"
        :disabled="readonly"
        @update:start-date="(value) => setFieldValue('startDate', value)"
        @update:end-date="(value) => setFieldValue('endDate', value)"
      />
    </div>

    <Divider />

    <VehicleImmutable :car="car" />

    <Divider />

    <div class="flex justify-end"></div>

    <div
      v-if="!readonly"
      class="flex flex-wrap items-center gap-2"
      :class="{ 'justify-end': blockNew, 'justify-between': !blockNew }"
    >
      <div v-if="!blockNew" class="flex flex-wrap gap-2">
        <CVButton
          outline
          variant="error"
          :disabled="isSaving || isMigrating"
          :is-loading="isDeleting"
          @click.prevent="$emit('onDelete')"
        >
          {{ t('deleteBlock') }}
        </CVButton>
        <CVButton
          v-if="canMigrate"
          outline
          :disabled="isSaving || isDeleting"
          :is-loading="isMigrating"
          @click.prevent="$emit('onMigrate')"
        >
          {{ t('convertToWorkshopAppointment') }}
        </CVButton>
      </div>

      <CVButton
        variant="default"
        type="submit"
        class="justify-self-end"
        :disabled="isDeleting || isMigrating"
        :is-loading="isSaving"
      >
        {{ t('save') }}
      </CVButton>
    </div>
  </form>
</template>

<script setup lang="ts">
import BookingRangePicker from '@/views/handover/components/overview/BookingRangePicker.vue';
import InputField from '@/components/InputField.vue';
import { useI18n } from 'vue-i18n';
import type { Car } from '@/entities/cars/car.entity';
import Divider from '@/components/Divider.vue';
import { useForm } from 'vee-validate';
import { maintenanceSchema } from '@/validation/maintenance.schema';
import { DateTime } from 'luxon';
import VehicleImmutable from '@/components/booking-forms/VehicleImmutable.vue';
import type { Allocation } from '@/entities/allocations/allocation.entity.ts';
import type { UpdateAllocationDto } from '@carvia/ros-client-types';
import { useAbility } from '@casl/vue';
import { subject } from '@casl/ability';

const props = defineProps<{
  car: Car;
  block?: Allocation;
  blockNew: boolean;
  startDate?: DateTime;
  isSaving: boolean;
  isDeleting: boolean;
  isMigrating: boolean;
  canMigrate: boolean;
}>();

const emit = defineEmits<{
  (e: 'onSubmit', value: UpdateAllocationDto): void;
  (e: 'onDelete'): void;
  (e: 'onMigrate'): void;
}>();

const { t } = useI18n();
const { can } = useAbility();

const readonly = !(props.block
  ? can('update', subject('Allocation', props.block))
  : can('create', 'Allocation'));

const newStartDate = props.startDate?.isValid
  ? (props.startDate as DateTime<true>)
  : DateTime.now();

const {
  handleSubmit,
  values: formValues,
  setFieldValue,
} = useForm({
  initialValues: {
    comment: props.block?.comment,
    startDate:
      props.block?.startDate ??
      newStartDate.set({ hour: 11, minute: 0, second: 0, millisecond: 0 }).toISO(),
    endDate:
      props.block?.endDate ??
      newStartDate.plus({ day: 1 }).set({ hour: 11, minute: 0, second: 0, millisecond: 0 }).toISO(),
  },
  validationSchema: maintenanceSchema,
});

const submit = handleSubmit((values) => {
  emit('onSubmit', {
    ...values,
    startDate: new Date(values.startDate),
    endDate: new Date(values.endDate),
  });
});
</script>

<i18n lang="json">
{
  "en": {
    "comment": "Comment",
    "save": "Save",
    "maintenanceStart": "Start",
    "maintenanceEnd": "End",
    "deleteBlock": "Delete Block",
    "convertToWorkshopAppointment": "Convert to workshop appointment"
  },
  "de": {
    "comment": "Kommentar",
    "save": "Speichern",
    "maintenanceStart": "Beginn",
    "maintenanceEnd": "Ende",
    "deleteBlock": "Block löschen",
    "convertToWorkshopAppointment": "In Werkstatttermin umwandeln"
  },
  "es": {
    "comment": "Comentario",
    "save": "Guardar",
    "maintenanceStart": "Inicio",
    "maintenanceEnd": "Fin",
    "deleteBlock": "Eliminar bloqueo",
    "convertToWorkshopAppointment": "Convertir en cita de taller"
  }
}
</i18n>
